import { useTranslation } from "react-i18next";
import { Check, Download, Eye } from "lucide-react";
import { ClickToCopyId } from "@/components/common/ClickToCopyId";
import { Badge, HoverPopover } from "@/components/ui";
import { cn } from "@/lib/utils";
import type { SettlementLineRow } from "@/services/adminSettlementService";
import {
  resolveInvoiceColumnState,
  resolvePaymentColumnState,
  settlementInvoiceReference,
} from "../settlementUi";

function IconTooltipButton({
  icon,
  label,
  disabled,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <HoverPopover content={label}>
      <button
        type="button"
        disabled={disabled}
        className={cn(
          "inline-flex h-7 w-7 items-center justify-center rounded-md text-surface-500 transition-colors",
          "hover:bg-surface-100 hover:text-surface-900",
          "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-primary-500",
          "disabled:pointer-events-none disabled:opacity-40",
        )}
        aria-label={label}
        onClick={(event) => {
          event.stopPropagation();
          onClick();
        }}
      >
        {icon}
      </button>
    </HoverPopover>
  );
}

function EmptyCell() {
  const { t } = useTranslation("payments");
  return <span className="text-surface-400">{t("shared.emptyCell")}</span>;
}

function PendingBadge({ label }: { label: string }) {
  return (
    <Badge variant="warning" className="font-normal">
      {label}
    </Badge>
  );
}

function DoneActions({
  doneLabel,
  reference,
  copyLabel,
  viewLabel,
  onView,
  downloadLabel,
  onDownload,
  actionsDisabled,
}: {
  doneLabel: string;
  reference?: string | null;
  copyLabel: string;
  viewLabel?: string;
  onView?: () => void;
  downloadLabel?: string;
  onDownload?: () => void;
  actionsDisabled?: boolean;
}) {
  return (
    <div className="flex items-center gap-1.5">
      <span
        className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-success-100 text-success-700"
        aria-label={doneLabel}
      >
        <Check className="h-3.5 w-3.5" aria-hidden />
      </span>
      {reference ? (
        <ClickToCopyId
          value={reference}
          title={copyLabel}
          ellipsis="start"
          className="max-w-[9rem] text-xs"
        />
      ) : null}
      {onView && viewLabel ? (
        <IconTooltipButton
          icon={<Eye className="h-3.5 w-3.5" aria-hidden />}
          label={viewLabel}
          disabled={actionsDisabled}
          onClick={onView}
        />
      ) : null}
      {onDownload && downloadLabel ? (
        <IconTooltipButton
          icon={<Download className="h-3.5 w-3.5" aria-hidden />}
          label={downloadLabel}
          disabled={actionsDisabled}
          onClick={onDownload}
        />
      ) : null}
    </div>
  );
}

export function SettlementInvoiceStatusCell({
  line,
  hasDocumentsRead,
  busy,
  onViewFePdf,
  onDownloadFePdf,
  onViewSupplierInvoice,
  onDownloadSupplierInvoice,
}: {
  line: SettlementLineRow;
  hasDocumentsRead: boolean;
  busy: boolean;
  onViewFePdf: (documentId: string) => void;
  onDownloadFePdf: (documentId: string) => void;
  onViewSupplierInvoice: (documentId: string) => void;
  onDownloadSupplierInvoice: (documentId: string) => void;
}) {
  const { t } = useTranslation("payments");
  const column = resolveInvoiceColumnState(line);

  if (column.kind === "empty") {
    return <EmptyCell />;
  }

  if (column.kind === "outgoing") {
    if (column.state === "to_invoice") {
      return <PendingBadge label={t("settlements.detail.invoiceToInvoice")} />;
    }

    const reference = settlementInvoiceReference(line);
    const documentId = line.invoice_document_id;
    const openDocument =
      documentId && hasDocumentsRead
        ? {
            view: () => onViewFePdf(documentId),
            download: () => onDownloadFePdf(documentId),
          }
        : null;

    return (
      <DoneActions
        doneLabel={t("settlements.detail.invoiceInvoiced")}
        reference={reference}
        copyLabel={t("settlements.detail.copyInvoiceReference")}
        viewLabel={t("settlements.detail.viewFePdf")}
        onView={openDocument?.view}
        downloadLabel={t("settlements.detail.downloadFePdf")}
        onDownload={openDocument?.download}
        actionsDisabled={busy}
      />
    );
  }

  if (column.state === "summary_not_sent") {
    return <PendingBadge label={t("settlements.detail.invoiceSummaryNotSent")} />;
  }

  if (column.state === "awaiting_invoice") {
    return <PendingBadge label={t("settlements.detail.invoiceAwaitingInvoice")} />;
  }

  const reference = settlementInvoiceReference(line);
  const documentId = line.invoice_document_id;
  const openDocument =
    documentId && hasDocumentsRead
      ? {
          view: () => onViewSupplierInvoice(documentId),
          download: () => onDownloadSupplierInvoice(documentId),
        }
      : null;

  return (
    <DoneActions
      doneLabel={t("settlements.detail.invoiceReceived")}
      reference={reference}
      copyLabel={t("settlements.detail.copyInvoiceReference")}
      viewLabel={t("settlements.detail.viewSupplierInvoice")}
      onView={openDocument?.view}
      downloadLabel={t("settlements.detail.downloadSupplierInvoice")}
      onDownload={openDocument?.download}
      actionsDisabled={busy}
    />
  );
}

export function SettlementPaymentStatusCell({ line }: { line: SettlementLineRow }) {
  const { t } = useTranslation("payments");
  const column = resolvePaymentColumnState(line);

  if (column.kind === "empty") {
    return <EmptyCell />;
  }

  if (column.kind === "incoming") {
    if (column.state === "awaiting") {
      return <PendingBadge label={t("settlements.detail.paymentAwaiting")} />;
    }

    const reference = line.collection_reference ?? line.collection_batch_id ?? null;

    return (
      <DoneActions
        doneLabel={t("settlements.detail.paymentReceived")}
        reference={reference}
        copyLabel={t("settlements.detail.copyCollectionReference")}
      />
    );
  }

  if (column.state === "not_available") {
    return (
      <Badge variant="default" className="font-normal text-surface-600">
        {t("settlements.detail.paymentNotAvailable")}
      </Badge>
    );
  }

  if (column.state === "to_pay") {
    return <PendingBadge label={t("settlements.detail.paymentToPay")} />;
  }

  const reference = line.payment_reference ?? line.payment_batch_id ?? null;

  return (
    <DoneActions
      doneLabel={t("settlements.detail.paymentPaid")}
      reference={reference}
      copyLabel={t("settlements.detail.copyPaymentReference")}
    />
  );
}
